import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import type { DashboardFinancieroResponse } from '@condo/contracts';
import { TrendingUp, TrendingDown, Landmark, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { formatPct } from '@/lib/format/number';
import { cn } from '@/lib/utils/cn';

const clpFmt = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  maximumFractionDigits: 0,
});

const DONUT_COLORS = {
  pagados: 'var(--color-moss)',
  pendientes: 'var(--color-ochre)',
  vencidos: 'var(--color-accent)',
} as const;

export function DashboardFinanciero({
  data,
}: {
  data: DashboardFinancieroResponse;
}) {
  const montoPendiente = data.montoPendiente;
  const fondoDelta = data.fondoDeltaMes;
  const fondoDeltaSign = fondoDelta >= 0 ? '+' : '';

  const donutData = [
    { name: 'Pagados', value: data.cargosPagados, fill: DONUT_COLORS.pagados },
    { name: 'Pendientes', value: data.cargosPendientes, fill: DONUT_COLORS.pendientes },
    { name: 'Vencidos', value: data.cargosVencidos, fill: DONUT_COLORS.vencidos },
  ];
  const totalCargosDonut = data.cargosPagados + data.cargosPendientes + data.cargosVencidos;

  return (
    <div className="space-y-6">
      {/* Row 1: 2 hero cards */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* Hero: Emitido / Cobrado / Pendiente */}
        <article className="stagger-fade relative overflow-hidden rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-6">
          <div
            aria-hidden
            className="absolute left-0 right-0 top-0 h-[3px] bg-[var(--color-primary)]"
          />
          <p className="eyebrow">Emitido este periodo</p>
          <p className="font-display-tight mt-3 text-4xl font-semibold tracking-tight text-[var(--color-ink)] tabular">
            {clpFmt.format(data.montoEmitido)}
          </p>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[var(--color-moss)]" />
              <span className="text-sm text-[var(--color-ink-muted)]">
                Cobrado:{' '}
                <span className="font-semibold text-[var(--color-moss)]">
                  {clpFmt.format(data.montoCobrado)}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'h-2 w-2 rounded-full',
                  montoPendiente > 0
                    ? 'bg-[var(--color-ochre)]'
                    : 'bg-[var(--color-ink-faint)]',
                )}
              />
              <span className="text-sm text-[var(--color-ink-muted)]">
                Pendiente:{' '}
                <span
                  className={cn(
                    'font-semibold',
                    montoPendiente > 0
                      ? 'text-[var(--color-ochre)]'
                      : 'text-[var(--color-ink-muted)]',
                  )}
                >
                  {clpFmt.format(montoPendiente)}
                </span>
              </span>
            </div>
          </div>
        </article>

        {/* Hero: Fondo de Reserva */}
        <article className="stagger-fade relative overflow-hidden rounded-md border border-[var(--color-primary)] bg-[var(--color-primary-50)] p-6">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full border border-[var(--color-primary)] opacity-30"
          />
          <div className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-[var(--color-primary)]" />
            <p className="eyebrow">Fondo de reserva</p>
          </div>
          <p className="font-display-tight mt-3 text-4xl font-semibold tracking-tight text-[var(--color-primary-700)] tabular">
            {clpFmt.format(data.fondoSaldo)}
          </p>
          <div className="mt-3 flex items-center gap-2">
            {fondoDelta >= 0 ? (
              <TrendingUp className="h-4 w-4 text-[var(--color-moss)]" />
            ) : (
              <TrendingDown className="h-4 w-4 text-[var(--color-accent)]" />
            )}
            <span
              className={cn(
                'text-sm font-semibold',
                fondoDelta >= 0
                  ? 'text-[var(--color-moss)]'
                  : 'text-[var(--color-accent)]',
              )}
            >
              {fondoDeltaSign}{clpFmt.format(fondoDelta)}
            </span>
            <span className="text-sm text-[var(--color-primary-700)] opacity-70">
              este mes
            </span>
          </div>
        </article>
      </div>

      {/* Row 2: 3 KPI cards + mini donut */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {/* Tasa de cobro */}
        <article className="stagger-fade relative overflow-hidden rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-5">
          <div
            aria-hidden
            className={cn(
              'absolute left-0 right-0 top-0 h-[3px]',
              data.tasaCobro >= 80
                ? 'bg-[var(--color-moss)]'
                : 'bg-[var(--color-ochre)]',
            )}
          />
          <p className="eyebrow">Tasa de cobro</p>
          <p className="font-display-tight mt-3 text-3xl font-semibold tracking-tight text-[var(--color-ink)] tabular">
            {formatPct(data.tasaCobro, 1)}
          </p>
          <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
            Del total emitido
          </p>
        </article>

        {/* Cargos pagados */}
        <article className="stagger-fade relative overflow-hidden rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-5">
          <div
            aria-hidden
            className="absolute left-0 right-0 top-0 h-[3px] bg-[var(--color-moss)]"
          />
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-[var(--color-moss)]" />
            <p className="eyebrow">Cargos pagados</p>
          </div>
          <p className="font-display-tight mt-3 text-3xl font-semibold tracking-tight text-[var(--color-ink)] tabular">
            {data.cargosPagados}
          </p>
          <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
            de {data.cantidadCargos} totales
          </p>
        </article>

        {/* Cargos vencidos */}
        <article className="stagger-fade relative overflow-hidden rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-5">
          <div
            aria-hidden
            className={cn(
              'absolute left-0 right-0 top-0 h-[3px]',
              data.cargosVencidos > 0
                ? 'bg-[var(--color-accent)]'
                : 'bg-[var(--color-moss)]',
            )}
          />
          <div className="flex items-center gap-2">
            {data.cargosVencidos > 0 ? (
              <AlertTriangle className="h-4 w-4 text-[var(--color-accent)]" />
            ) : (
              <Clock className="h-4 w-4 text-[var(--color-ink-muted)]" />
            )}
            <p className="eyebrow">Cargos vencidos</p>
          </div>
          <p
            className={cn(
              'font-display-tight mt-3 text-3xl font-semibold tracking-tight tabular',
              data.cargosVencidos > 0
                ? 'text-[var(--color-accent)]'
                : 'text-[var(--color-ink)]',
            )}
          >
            {data.cargosVencidos}
          </p>
          <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
            de {data.cantidadCargos} totales
          </p>
        </article>

        {/* Mini donut */}
        <article className="stagger-fade relative overflow-hidden rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-5">
          <p className="eyebrow">Distribucion de cargos</p>
          {totalCargosDonut === 0 ? (
            <p className="mt-4 text-sm text-[var(--color-ink-muted)]">
              Sin cargos en este periodo
            </p>
          ) : (
            <div className="mt-2 flex items-center gap-3">
              <div className="h-24 w-24 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      innerRadius={24}
                      outerRadius={40}
                      strokeWidth={0}
                    >
                      {donutData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-1.5 text-[0.6875rem]">
                {donutData.map((d) => (
                  <li key={d.name} className="flex items-center gap-1.5">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: d.fill }}
                    />
                    <span className="text-[var(--color-ink-muted)]">
                      {d.name}:{' '}
                      <span className="font-semibold text-[var(--color-ink)]">
                        {d.value}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </article>
      </div>
    </div>
  );
}
